import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import type { Language } from '../types';
import { _t } from '../lib/i18n';
import { pullToRefreshIndicatorClass } from '../styles/uiStandards';

const MOBILE_MAX_WIDTH_PX = 767;
const PULL_ACTIVATE_PX = 72;
const PULL_MAX_PX = 112;
const PULL_RESISTANCE = 0.45;

type RefreshHandler = (() => Promise<void> | void) | null;

type PullToRefreshContextValue = {
  register: (handler: RefreshHandler) => void;
};

const PullToRefreshContext = createContext<PullToRefreshContextValue | null>(null);

function useIsMobileViewport() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`).matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return mobile;
}

function PullToRefreshIndicator({
  lang,
  handlerRef,
  scrollContainerSelector,
  indicatorClassName = pullToRefreshIndicatorClass,
}: {
  lang: Language;
  handlerRef: MutableRefObject<RefreshHandler>;
  scrollContainerSelector?: string;
  indicatorClassName?: string;
}) {
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const pullPxRef = useRef(0);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const refreshingRef = useRef(false);

  refreshingRef.current = refreshing;

  const setPull = (value: number) => {
    pullPxRef.current = value;
    setPullPx(value);
  };

  const resetPull = useCallback(() => {
    pullingRef.current = false;
    scrollElRef.current = null;
    setPull(0);
  }, []);

  const scrollTop = () => {
    if (scrollContainerSelector && scrollElRef.current) {
      return scrollElRef.current.scrollTop;
    }
    return window.scrollY;
  };

  useEffect(() => {
    const finishPull = () => {
      if (!pullingRef.current) {
        return;
      }
      pullingRef.current = false;

      const handler = handlerRef.current;
      if (!handler || pullPxRef.current < PULL_ACTIVATE_PX || refreshingRef.current) {
        resetPull();
        return;
      }

      setRefreshing(true);
      refreshingRef.current = true;
      setPull(PULL_ACTIVATE_PX);
      void Promise.resolve(handler())
        .catch(() => undefined)
        .finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          setPull(0);
          scrollElRef.current = null;
        });
    };

    const onTouchStart = (event: TouchEvent) => {
      if (!handlerRef.current || refreshingRef.current) {
        return;
      }

      if (scrollContainerSelector) {
        const target = event.target as HTMLElement | null;
        scrollElRef.current = target?.closest(scrollContainerSelector) ?? null;
        if (!scrollElRef.current) {
          return;
        }
      } else {
        scrollElRef.current = null;
      }

      if (scrollTop() > 0) {
        return;
      }
      startYRef.current = event.touches[0]?.clientY ?? 0;
      pullingRef.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pullingRef.current || refreshingRef.current || !handlerRef.current) {
        return;
      }
      if (scrollTop() > 0) {
        resetPull();
        return;
      }

      const currentY = event.touches[0]?.clientY ?? 0;
      const delta = currentY - startYRef.current;
      if (delta <= 0) {
        resetPull();
        return;
      }

      event.preventDefault();
      setPull(Math.min(delta * PULL_RESISTANCE, PULL_MAX_PX));
    };

    const onTouchEnd = () => {
      finishPull();
    };

    // Visiting card flip/slider handlers can swallow touchend — always clear a half-pull.
    const onTouchEndCapture = () => {
      if (pullingRef.current) {
        finishPull();
        return;
      }
      if (pullPxRef.current > 0 && !refreshingRef.current) {
        resetPull();
      }
    };

    const onScrollCapture = (event: Event) => {
      if (refreshingRef.current || pullPxRef.current <= 0) {
        return;
      }
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const inContainer = scrollContainerSelector
        ? target.matches(scrollContainerSelector) || Boolean(target.closest(scrollContainerSelector))
        : true;
      if (inContainer) {
        resetPull();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', resetPull);
    document.addEventListener('touchend', onTouchEndCapture, true);
    document.addEventListener('touchcancel', onTouchEndCapture, true);
    document.addEventListener('scroll', onScrollCapture, { capture: true, passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', resetPull);
      document.removeEventListener('touchend', onTouchEndCapture, true);
      document.removeEventListener('touchcancel', onTouchEndCapture, true);
      document.removeEventListener('scroll', onScrollCapture, true);
    };
  }, [handlerRef, resetPull, scrollContainerSelector]);

  const visible = pullPx > 0 || refreshing;
  const ready = pullPx >= PULL_ACTIVATE_PX || refreshing;

  return createPortal(
    <div
      id="pull-to-refresh-indicator"
      className={indicatorClassName}
      style={{
        transform: `translateY(${visible ? pullPx : 0}px)`,
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden',
      }}
      aria-hidden={!visible}
    >
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)]/95 text-[var(--accent-primary)] shadow-md backdrop-blur ${
          ready ? 'border-[var(--accent-primary)]/40' : ''
        }`}
      >
        <Loader2
          size={18}
          className={refreshing ? 'animate-spin' : ''}
          style={refreshing ? undefined : { transform: `rotate(${Math.min(1, pullPx / PULL_ACTIVATE_PX) * 180}deg)` }}
          aria-hidden
        />
      </div>
      <span className="sr-only">{_t('common.pullToRefresh', lang)}</span>
    </div>,
    document.body,
  );
}

type PullToRefreshProviderProps = {
  lang: Language;
  enabled?: boolean;
  /** Pull-to-refresh reads scrollTop from this container instead of the window. */
  scrollContainerSelector?: string;
  indicatorClassName?: string;
  children: ReactNode;
};

export function PullToRefreshProvider({
  lang,
  enabled = true,
  scrollContainerSelector,
  indicatorClassName,
  children,
}: PullToRefreshProviderProps) {
  const handlerRef = useRef<RefreshHandler>(null);
  const isMobile = useIsMobileViewport();
  const active = enabled && isMobile;

  const register = useCallback((handler: RefreshHandler) => {
    handlerRef.current = handler;
  }, []);

  useEffect(() => {
    if (!active) {
      document.documentElement.classList.remove('cow-ptr-active');
      return;
    }
    document.documentElement.classList.add('cow-ptr-active');
    return () => document.documentElement.classList.remove('cow-ptr-active');
  }, [active]);

  return (
    <PullToRefreshContext.Provider value={{ register }}>
      {children}
      {active ? (
        <PullToRefreshIndicator
          lang={lang}
          handlerRef={handlerRef}
          scrollContainerSelector={scrollContainerSelector}
          indicatorClassName={indicatorClassName}
        />
      ) : null}
    </PullToRefreshContext.Provider>
  );
}

export function useRegisterPullToRefresh(handler: RefreshHandler) {
  const context = useContext(PullToRefreshContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!context) {
      return;
    }

    if (!handler) {
      context.register(null);
      return;
    }

    context.register(async () => {
      await handlerRef.current?.();
    });

    return () => context.register(null);
  }, [context, handler]);
}
